import { Sites } from '@ps/platform-message-framework/common/Sites';
import type { CasinoNewGames } from '@ps/web-martech-cms-types';
import { useTrackers } from '@ps/web-shared-analytics';
import { StarsButton, Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { camelCase } from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EligibleFeatures, NewGame } from '@swf/casino';
import type { FeatureVariant } from '@swf/casino';
import ExclusiveGame from '@swf/core/assets/pokerstars-logo-standalone.svg';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { MarketingTag } from '@swf/core/types/CasinoGame';
import type { CasinoGame, CasinoTable } from '@swf/core/types/CasinoGame';
import type { CasinoGameList } from '@swf/core/types/CasinoGameList';

import {
  TXTCLI_Play,
  TXTWCL_Features,
  TXTWCL_Join,
  TXTWCL_NEW
} from './GameFeatures.messages.json';
import styles from './GameFeatures.scss';

export interface Props {
  className?: string;
  data: CasinoTable;
  game?: CasinoGame;
  fsbLaunch?: boolean;
  raceId?: string;
  detailsOnly?: boolean;
  showFields?: CasinoGameList['renderInfo'];
  carouselIcon?: CasinoNewGames['carousel_icon'];
  shortText?: boolean;
  featureVariant?: FeatureVariant[];
}

const icons = {
  [MarketingTag.New]: { i18n: TXTWCL_NEW, icon: <NewGame /> },
  [MarketingTag.Exclusive]: { i18n: undefined, icon: <ExclusiveGame /> }
};

const GameFeatures: React.FC<Props> = (props) => {
  const {
    className,
    data,
    detailsOnly = false,
    game,
    fsbLaunch,
    carouselIcon,
    raceId,
    featureVariant,
    shortText,
    ...rest
  } = props;
  const siteId = useSelector(select.cms.getSiteId);
  const { casinoGameLaunched } = useTrackers();
  const {
    casino: { launch }
  } = useDispatch<Dispatch>();
  const { modal } = useDispatch<Dispatch>();
  const { id, playMoney } = data;

  const onPlayClickHandler = () => {
    modal.close('/casino/game-info');
    launch({ table: { id, playMoney } as CasinoTable, fsbLaunch, raceId, game });
    if (game) casinoGameLaunched({ game });
  };

  const icon = carouselIcon && icons[carouselIcon];
  return (
    <div className={classNames(styles.gameInfo, className, carouselIcon && styles.marketingInfo)}>
      {!detailsOnly && <h1 className={styles.gameTitle}>{game?.name}</h1>}
      {icon && (
        <div
          className={classNames(
            styles.marketingIcon,
            styles[camelCase(`${carouselIcon}-icon`) as keyof typeof styles]
          )}
        >
          {icon.icon}
          {icon.i18n ? <Translation tagName="span" {...icon.i18n} /> : null}
        </div>
      )}
      <div className={styles.featuresBlock}>
        {!detailsOnly && (
          <>
            <StarsButton
              className={styles.gameButton}
              onClick={() => onPlayClickHandler()}
              translation={siteId === Sites.GR ? TXTWCL_Join : TXTCLI_Play}
            />
            <span className={styles.gameInfoSh}>
              <Translation {...TXTWCL_Features} />
            </span>
          </>
        )}
        <EligibleFeatures
          featureVariant={featureVariant}
          shortText={shortText}
          setBetFeatureCount={() => true}
          siteId={siteId}
          data={data}
          {...rest}
        />
      </div>
    </div>
  );
};

export default GameFeatures;
